import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";
import { FadeUp, MaskLines, Em, Eyebrow } from "@/components/site/Motion";
import { FlagRow } from "@/components/site/Flags";
import { OpenTag, useOfficeOpen } from "@/components/site/OpenTag";
import { WINGS } from "@/lib/site";

const fmt = (tz) => new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(new Date());

const WingClock = ({ wing, flags, label, delay }) => {
  const [t, setT] = useState(fmt(wing.tz));
  useEffect(() => {
    const id = setInterval(() => setT(fmt(wing.tz)), 1000);
    return () => clearInterval(id);
  }, [wing.tz]);
  const awake = useOfficeOpen(wing.tz);
  return (
    <FadeUp delay={delay} className={`flex flex-col justify-between gap-6 border-2 border-[#0E0E10] p-6 transition-colors duration-300 sm:p-9 ${awake ? "bg-[#D4FF3D]" : "bg-[#0E0E10] text-[#F1ECE2]"}`} data-testid={`wing-clock-${label}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3"><FlagRow ids={flags} size="w-6" /><OpenTag tz={wing.tz} testid={`open-tag-${label}`} className={awake ? "ring-1 ring-[#0E0E10]/30" : ""} /></div>
        {awake ? <Sun className="h-6 w-6 shrink-0" data-testid={`sun-${label}`} /> : <Moon className="h-6 w-6 shrink-0" data-testid={`moon-${label}`} />}
      </div>
      <div>
        <p className="font-mono-x text-xs uppercase tracking-[0.2em] opacity-80">{label} wing</p>
        <p className="font-mono-x mt-2 text-4xl font-bold tabular-nums sm:text-5xl" data-testid={`clock-${label}`}>{t}</p>
        <a href={`tel:${wing.tel}`} className="ul-draw mt-2 inline-block text-sm opacity-80">{wing.phone}</a>
      </div>
    </FadeUp>
  );
};

export const FollowTheSun = () => (
  <section className="px-5 py-24 sm:px-8 lg:py-32" data-testid="follow-sun-section">
    <div className="mx-auto max-w-[1400px]">
      <Eyebrow className="text-[#c2380f]">follow the sun</Eyebrow>
      <MaskLines as="h2" lines={["one wing sleeps.", <Em className="text-[#2234F5]">the other dials.</Em>]}
        className="font-display mt-6 text-4xl font-bold leading-[0.95] tracking-[-0.03em] sm:text-6xl" />
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-5"><WingClock wing={WINGS.pitsus} flags={["us"]} label="usa" delay={0} /></div>
        <FadeUp delay={0.1} className="flex flex-col justify-center gap-4 lg:col-span-2">
          <p className="text-center leading-relaxed text-[#3d3a35]">Between the two of us, it's always business hours somewhere on this line.</p>
          <p className="font-mono-x text-center text-xs uppercase tracking-[0.2em] text-[#c2380f]">no clocks-off clause</p>
        </FadeUp>
        <div className="lg:col-span-5 lg:mt-16"><WingClock wing={WINGS.pitsind} flags={["uk", "eu", "apac"]} label="india" delay={0.2} /></div>
      </div>
    </div>
  </section>
);
